import { useEffect, useRef } from 'react'
import { Alert, Box, LinearProgress, Typography } from '@mui/material'
import { JOB_STATUS } from '@api/types/adminJobs'
import { useJobRun, useRefreshJobViews } from '../hooks/useAdminJobs'
import { formatDuration, isFinished, parseMetadata, summarize } from '../popularTimes'

interface JobRunStatusProps {
  jobId: string
  /** What was launched, e.g. "Recompute". */
  title: string
  onClose?: () => void
}

/**
 * Follows one launched run (polls every 2 s) and shows running → result or error. When it finishes it refreshes the
 * job list, the history and the popular-times panels.
 */
export function JobRunStatus({ jobId, title, onClose }: JobRunStatusProps) {
  const { data: run, error } = useJobRun(jobId)
  const refresh = useRefreshJobViews()
  const finishedRef = useRef(false)

  const finished = !!run && isFinished(run.status)
  useEffect(() => {
    if (finished && !finishedRef.current) {
      finishedRef.current = true
      refresh()
    }
  }, [finished, refresh])

  if (error) return <Alert severity="warning" onClose={onClose}>{title}: could not read the run's status.</Alert>
  if (!run || !finished) {
    return (
      <Alert severity="info" icon={false} onClose={onClose}>
        <Typography variant="body2" sx={{ mb: 1 }}>{title}: running…</Typography>
        <LinearProgress />
      </Alert>
    )
  }

  const meta = parseMetadata(run)
  if (run.status === JOB_STATUS.Failed || run.status === JOB_STATUS.Cancelled) {
    return (
      <Alert severity="error" onClose={onClose}>
        {title} {run.status === JOB_STATUS.Failed ? 'failed' : 'was cancelled'} after {formatDuration(run.duration)}
        {run.errorMessage ? `: ${run.errorMessage}` : '.'}
      </Alert>
    )
  }
  return (
    <Alert severity="success" onClose={onClose}>
      <Box>
        {title} finished in {formatDuration(run.duration)} ({run.recordsProcessed.toLocaleString()} records).
      </Box>
      {meta?.result && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5, wordBreak: 'break-word' }}>
          {summarize(meta.result)}
        </Typography>
      )}
    </Alert>
  )
}
